import { FC, useContext, useEffect } from 'react';

import { useQuery } from '@apollo/client';
import {
  BackwardIcon,
  Button,
  H1,
  H3,
  InfoBlock,
  Input,
  NumberInput,
  RadioInput,
  Separator,
  TextArea,
} from '@smartshell/ui-kit';
import { Controller, useForm } from 'react-hook-form';

import { AuthContext } from 'auth';
import { USER_CLUBS } from 'auth/schema';
import { IUserClubsResponse } from 'auth/types';
import { useAdvertisementMutations } from 'hooks';
import { LocalizationContext } from 'localization';
import { SettingsContext } from 'settings';

import { EAdvertisementStatus } from '../../types';
import s from './AdvertisementManagerMobile.module.scss';
import { AdvertisementImage } from './components';
import { DEFAULT_VALUES } from './constants';
import { IAdvertisementManagerMobileProps, IFormValues } from './interfaces';

export const AdvertisementManagerMobile: FC<IAdvertisementManagerMobileProps> =
  ({ onClose, setOpenAdvertisementPublished, mode, advertisement }) => {
    const { t } = useContext(LocalizationContext);
    const {
      state: {
        settings: { currency },
      },
    } = useContext(SettingsContext);
    const {
      data: { tokens },
    } = useContext(AuthContext);

    const {
      control,
      handleSubmit,
      reset,
      formState: { isValid },
    } = useForm<IFormValues>({
      mode: 'onChange',
      defaultValues: DEFAULT_VALUES,
    });
    const {
      handledCreateAdvertisement,
      creating,
      handledUpdateAdvertisement,
      updating,
    } = useAdvertisementMutations(t);

    const { data } = useQuery<IUserClubsResponse>(USER_CLUBS, {
      fetchPolicy: 'cache-only',
    });
    const encodedToken = atob(tokens?.access_token?.split('.')[1] ?? '');
    const clubId = encodedToken ? JSON.parse(encodedToken)?.company_id : null;

    const clubInfo = data?.userClubs?.find((club) => club.id === clubId);

    const onSubmit = (values: IFormValues): void => {
      if (mode === 'edit' && advertisement) {
        handledUpdateAdvertisement(advertisement?.id, {
          title: values.title.trim(),
          description: values.description.trim(),
          category_id: values.category === 'good' ? 1 : 2,
          images: values.images,
          price: Number(values.price),
          contact: values?.contact?.trim(),
        }).then((data) => {
          if (data?.data?.updateAdvertisement?.id) {
            setOpenAdvertisementPublished(true);
            onClose?.();
          }
        });
      } else {
        handledCreateAdvertisement({
          title: values.title.trim(),
          description: values.description.trim(),
          category_id: values.category === 'good' ? 1 : 2,
          images: values.images,
          price: Number(values.price),
          contact: values?.contact?.trim(),
        }).then((data) => {
          if (data?.data?.createAdvertisement?.id) {
            setOpenAdvertisementPublished(true);
            onClose?.();
          }
        });
      }
    };

    useEffect(() => {
      if (mode === 'edit' && advertisement) {
        reset({
          title: advertisement.title,
          description: advertisement.description,
          category:
            advertisement.category.slug === 'goods' ? 'good' : 'service',
          images: advertisement.images,
          price: advertisement.price,
          contact: advertisement.contact,
        });
      }

      if (mode === 'create') {
        reset(DEFAULT_VALUES);
      }
    }, [mode, advertisement, reset]);

    return (
      <div className={s.container}>
        <div className={s.link} onClick={() => onClose()}>
          <BackwardIcon />
          {t('back')}
        </div>
        <div className={s.content}>
          <H1>
            {mode === 'create'
              ? t('advertisement.manager.title')
              : t('advertisement.manager.title_2')}
          </H1>
          {advertisement?.status === EAdvertisementStatus.REJECTED && (
            <InfoBlock
              className={s.info_block}
              color="red"
              iconName="CircleCloseIcon"
              title={t('canceled')}
              message={advertisement?.moderation?.comment}
            />
          )}
          <form className={s.form} onSubmit={handleSubmit(onSubmit)}>
            <div className={s.frame}>
              <div className={s.frame_block}>
                <div className={s.title_block}>
                  <H3>{t('advertisement.manager.form.title')}</H3>
                  <Controller
                    name="title"
                    control={control}
                    render={({ field, fieldState: { error } }) => (
                      <Input
                        {...field}
                        maxLength={56}
                        placeholder={t(
                          'advertisement.manager.form.title.placeholder'
                        )}
                        error={error?.message}
                        bordered
                        variant="secondary"
                      />
                    )}
                    rules={{
                      required: t('errors.required'),
                      minLength: {
                        value: 3,
                        message: t('symbols.min', 3),
                      },
                      maxLength: {
                        value: 56,
                        message: t('symbols.max', 56),
                      },
                    }}
                  />
                </div>

                <Separator />

                <div className={s.category_block}>
                  <H3>{t('category')}</H3>
                  <Controller
                    name="category"
                    control={control}
                    render={({ field }) => (
                      <div className={s.category}>
                        <RadioInput
                          {...field}
                          label={t('good')}
                          checked={field.value === 'good'}
                          value="good"
                        />
                        <RadioInput
                          {...field}
                          label={t('service')}
                          checked={field.value === 'service'}
                          value="service"
                        />
                      </div>
                    )}
                    rules={{ required: t('errors.required') }}
                  />
                </div>
              </div>
            </div>

            <div className={s.frame}>
              <H3>{t('advertisement.manager.form.description')}</H3>
              <Controller
                name="description"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextArea
                    title={''}
                    {...field}
                    limit={2048}
                    placeholder={t(
                      'advertisement.manager.form.description.placeholder'
                    )}
                    error={error?.message}
                    bordered
                    variant="secondary"
                  />
                )}
                rules={{
                  required: t('errors.required'),
                  minLength: {
                    value: 1,
                    message: t('symbols.min', 1),
                  },
                  maxLength: {
                    value: 2048,
                    message: t('symbols.max', 2048),
                  },
                }}
              />
            </div>

            <div className={s.frame}>
              <Controller
                render={({ field, fieldState: { error } }) => (
                  <AdvertisementImage
                    value={field.value}
                    onChange={field.onChange}
                    maxCount={5}
                    error={error?.message}
                  />
                )}
                name="images"
                control={control}
                rules={{ required: t('errors.required') }}
              />
            </div>

            <div className={s.frame}>
              <H3>
                {t('tariff.price')}, {currency.sign}
              </H3>
              <Controller
                name="price"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <NumberInput
                    {...field}
                    placeholder="100"
                    bordered
                    variant="secondary"
                    error={error?.message}
                  />
                )}
                rules={{
                  required: t('errors.required'),
                  min: {
                    value: 1,
                    message: t('errors.min', 1, currency.sign),
                  },
                  max: {
                    value: 999999999,
                    message: t('errors.max', '999999999', currency.sign),
                  },
                }}
              />
            </div>

            <div className={s.frame}>
              <H3>{t('contacts')}</H3>
              <Controller
                name="contact"
                control={control}
                render={({ field, fieldState: { error } }) => (
                  <TextArea
                    title={''}
                    {...field}
                    limit={256}
                    placeholder={t('advertisement.add.contact')}
                    bordered
                    variant="secondary"
                    error={error?.message}
                  />
                )}
                rules={{
                  required: t('errors.required'),
                  minLength: {
                    value: 3,
                    message: t('symbols.min', 3),
                  },
                  maxLength: {
                    value: 256,
                    message: t('symbols.max', 256),
                  },
                }}
              />
            </div>

            {clubInfo && (
              <div className={s.frame}>
                <H3>{t('advertisement.manager.form.info')}</H3>
                <div className={s.club_info}>
                  <span className={s.name}>{clubInfo?.name}</span>
                  <span className={s.address}>{clubInfo?.address}</span>
                </div>
              </div>
            )}

            <div className={s.buttons_block}>
              <Button variant="secondary" onClick={onClose} fullWidth>
                {t('cancel')}
              </Button>
              <Button
                disabled={!isValid}
                isLoading={creating || updating}
                type="submit"
                fullWidth
              >
                {t('published')}
              </Button>
            </div>
          </form>
        </div>
      </div>
    );
  };
